import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, useTheme, Grid, LinearProgress, Chip, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../../components/global/Header";
import FormListToolbar from "../../../../components/global/Toolbar";
import useSWR from 'swr';
import api from '../../../../services/api';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { EmptyState } from '../../../../components/global';

const CategoryPerformance = () => {
  const theme = useTheme();
  const [selectedProduction, setSelectedProduction] = useState(null);

  const { data: productions } = useSWR('/productions');
  
  const { data, error, isLoading } = useSWR(
    selectedProduction?.id
      ? `/reports/category-performance?productionId=${selectedProduction.id}`
      : '/reports/category-performance',
    async (url) => {
      const response = await api.get(url);
      return response;
    }
  );

  useEffect(() => {
    if (productions && productions.length > 0) {
      const savedProduction = localStorage.getItem('selectedProduction');
      if (savedProduction) {
        try {
          const parsedProduction = JSON.parse(savedProduction);
          const production = productions.find(p => p.id === parsedProduction.id);
          if (production) {
            setSelectedProduction(production);
          }
        } catch (e) {
          // Invalid saved production, ignore
        }
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedProduction(null);
    } else {
      const production = productions?.find((p) => p.id === value);
      setSelectedProduction(production || null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Header title="Απόδοση Κατηγοριών" module="Αναφορές" />
        <EmptyState title="Δεν ήταν δυνατή η φόρτωση των δεδομένων" />
      </Box>
    );
  }

  const categories = data || [];

  const pieData = categories.map((cat) => ({
    id: cat.categoryName,
    label: cat.categoryName,
    value: cat.totalExpenses,
  }));

  const barData = categories.map((cat) => ({
    category: cat.categoryName.length > 15 ? cat.categoryName.substring(0, 15) + '...' : cat.categoryName,
    budget: cat.budget,
    actual: cat.actualExpenses,
    predicted: cat.predictedExpenses,
  }));

  const columns = [
    {
      field: 'categoryName',
      headerName: 'Κατηγορία',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'budget',
      headerName: 'Προϋπολογισμός',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'actualExpenses',
      headerName: 'Πραγματικά',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'predictedExpenses',
      headerName: 'Προβλέψεις',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'totalExpenses',
      headerName: 'Σύνολο',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'variance',
      headerName: 'Απόκλιση',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        // Positive variance (budget > totalExpenses) = under budget = good (green)
        // Negative variance (budget < totalExpenses) = over budget = bad (red)
        const color = value < 0 ? theme.palette.error.main : theme.palette.success.main;
        return (
          <Typography sx={{ color, fontWeight: 600 }}>
            {formatCurrency(value)}
          </Typography>
        );
      },
    },
    {
      field: 'utilization',
      headerName: 'Χρήση %',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params.value;
        const isOverBudget = value > 100;
        const isWarning = value > 80 && value <= 100;
        
        const getStatusColor = () => {
          if (isOverBudget) return theme.palette.error.main;
          if (isWarning) return theme.palette.warning.main;
          return theme.palette.success.main;
        };
        
        const getStatusIcon = () => {
          if (isOverBudget) return <ErrorOutlineIcon sx={{ fontSize: 16 }} />;
          if (isWarning) return <WarningIcon sx={{ fontSize: 16 }} />;
          return <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />;
        };
        
        const getStatusLabel = () => {
          if (isOverBudget) return 'Υπέρ';
          if (isWarning) return 'Προσοχή';
          return 'Καλό';
        };
        
        const displayValue = Math.min(value, 100);
        const overBudgetAmount = value > 100 ? value - 100 : 0;
        
        return (
          <Box sx={{ width: '100%', px: 1.5, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Chip
                icon={getStatusIcon()}
                label={getStatusLabel()}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: getStatusColor(),
                  color: theme.palette.getContrastText(getStatusColor()),
                  '& .MuiChip-icon': {
                    color: theme.palette.getContrastText(getStatusColor()),
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: getStatusColor(),
                  fontSize: '0.95rem',
                }}
              >
                {value.toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={displayValue}
                sx={{
                  height: 10,
                  borderRadius: 2,
                  backgroundColor: theme.palette.action.hover,
                  overflow: 'hidden',
                  boxShadow: `inset 0 1px 2px ${theme.palette.divider}`,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getStatusColor(),
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: `0 0 8px ${getStatusColor()}40`,
                  },
                }}
              />
              {isOverBudget && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    height: '100%',
                    width: `${overBudgetAmount}%`,
                    backgroundColor: theme.palette.error.dark,
                    borderRadius: '0 2px 2px 0',
                    opacity: 0.6,
                  }}
                />
              )}
            </Box>
            {isOverBudget && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: 'block',
                  textAlign: 'right',
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              >
                +{overBudgetAmount.toFixed(1)}% πάνω από προϋπολογισμό
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'itemsCount',
      headerName: 'Είδη Δαπάνης',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Header title="Απόδοση Κατηγοριών" module="Αναφορές" subtitle="Ανάλυση εξόδων ανά κατηγορία" />

      {/* Production Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="prod-label">Παραγωγή</InputLabel>
          <Select
            labelId="prod-label"
            id="prod-id"
            label="Παραγωγή"
            value={selectedProduction?.id || ''}
            onChange={handleProductionChange}
          >
            <MenuItem value="">
              <em>Όλες οι Παραγωγές</em>
            </MenuItem>
            {productions?.map((production) => (
              <MenuItem key={production.id} value={production.id}>
                {production.p_descr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400, boxShadow: theme.shadows[3] }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Κατανομή Εξόδων
            </Typography>
            {pieData.length > 0 ? (
              <Box sx={{ height: 320 }}>
                <ResponsivePie
                  data={pieData}
                  margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                  innerRadius={0.5}
                  padAngle={2}
                  cornerRadius={4}
                  activeOuterRadiusOffset={8}
                  colors={{ scheme: 'set3' }}
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor={theme.palette.text.primary}
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: 'color' }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                  valueFormat={formatCurrency}
                  theme={{
                    text: {
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                    },
                  }}
                />
              </Box>
            ) : (
              <EmptyState title="Δεν υπάρχουν δεδομένα" />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400, boxShadow: theme.shadows[3] }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Προϋπολογισμός vs Έξοδα
            </Typography>
            {barData.length > 0 ? (
              <Box sx={{ height: 320 }}>
                <ResponsiveBar
                  data={barData}
                  keys={['budget', 'actual', 'predicted']}
                  indexBy="category"
                  margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
                  padding={0.3}
                  valueFormat={formatCurrency}
                  colors={[theme.palette.success.main, theme.palette.info.main, theme.palette.warning.main]}
                  axisLeft={{
                    format: (value) => formatCurrency(value),
                  }}
                  axisBottom={{
                    tickRotation: -45,
                  }}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'top-right',
                      direction: 'column',
                      translateX: 100,
                      itemWidth: 80,
                      itemHeight: 20,
                    },
                  ]}
                  theme={{
                    text: {
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                    },
                    grid: {
                      line: {
                        stroke: theme.palette.divider,
                      },
                    },
                  }}
                />
              </Box>
            ) : (
              <EmptyState title="Δεν υπάρχουν δεδομένα" />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Paper sx={{ p: 2, borderRadius: 3, boxShadow: theme.shadows[3] }}>
        <FormListToolbar disableAdd />
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={categories.map((cat, index) => ({ ...cat, id: index }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default CategoryPerformance;

